import styled from 'styled-components';

import RatingStarSvg from '../../assets/icons/icon-rating-star.svg?react';

type ProfileRatingValueTone = 'default' | 'emphasis' | 'subdued';

type ProfileRatingProps = {
  dataUi?: string;
  fillLevels?: ReadonlyArray<number>;
  gapPx?: number;
  starGapPx?: number;
  starSizePx: number;
  valueFontSizePx: number;
  valueFontWeight?: number;
  valueLabel: string;
  valueTone?: ProfileRatingValueTone;
};

function clampFillLevel(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
}

function resolveStarFillLevels(
  valueLabel: string,
  fillLevels?: ReadonlyArray<number>,
): ReadonlyArray<number> {
  if (fillLevels && fillLevels.length > 0) {
    return fillLevels.map(clampFillLevel);
  }

  const normalizedValueLabel = valueLabel.trim().replace(',', '.');
  const parsedRatingValue = Number.parseFloat(normalizedValueLabel);
  const ratingValue = Number.isFinite(parsedRatingValue)
    ? parsedRatingValue
    : 0;

  return Array.from({ length: 5 }, (_, index) =>
    clampFillLevel(ratingValue - index),
  );
}

const RatingRow = styled.div.attrs<{ $dataUi?: string }>(({ $dataUi }) => ({
  'data-ui': $dataUi,
}))<{ $gapPx: number }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $gapPx, theme }) => theme.scale.px($gapPx)};
`;

const StarCluster = styled.div<{ $starGapPx: number }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $starGapPx, theme }) => theme.scale.px($starGapPx)};
`;

const StarSlot = styled.span<{ $sizePx: number }>`
  position: relative;
  width: ${({ $sizePx, theme }) => theme.scale.px($sizePx)};
  height: ${({ $sizePx, theme }) => theme.scale.px($sizePx)};
`;

const RatingStarBase = styled(RatingStarSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})<{ $sizePx: number }>`
  width: ${({ $sizePx, theme }) => theme.scale.px($sizePx)};
  height: ${({ $sizePx, theme }) => theme.scale.px($sizePx)};
  display: block;
  color: ${({ theme }) => theme.colors.support.rating};

  & path {
    fill: currentColor;
  }
`;

const StarFillMask = styled.span<{ $fillWidth: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $fillWidth }) => $fillWidth};
  height: 100%;
  overflow: hidden;
`;

const RatingStarFill = styled(RatingStarBase)`
  color: ${({ theme }) => theme.colors.brand.primary};
`;

const RatingValue = styled.span<{
  $fontSizePx: number;
  $fontWeight: number;
  $valueTone: ProfileRatingValueTone;
}>`
  font-size: ${({ $fontSizePx, theme }) => theme.scale.px($fontSizePx)};
  font-weight: ${({ $fontWeight }) => $fontWeight};
  color: ${({ $valueTone, theme }) => {
    if ($valueTone === 'emphasis') {
      return theme.colors.border.emphasis;
    }

    if ($valueTone === 'subdued') {
      return theme.colors.text.subdued;
    }

    return theme.colors.text.primary;
  }};
`;

export function ProfileRating({
  dataUi,
  fillLevels,
  gapPx = 5,
  starGapPx = 4,
  starSizePx,
  valueFontSizePx,
  valueFontWeight = 400,
  valueLabel,
  valueTone = 'default',
}: ProfileRatingProps) {
  const resolvedFillLevels = resolveStarFillLevels(valueLabel, fillLevels);

  return (
    <RatingRow $dataUi={dataUi} $gapPx={gapPx}>
      <StarCluster $starGapPx={starGapPx}>
        {resolvedFillLevels.map((fillLevel, index) => {
          const fillWidth = `${fillLevel * 100}%`;

          return (
            <StarSlot key={index} $sizePx={starSizePx}>
              <RatingStarBase $sizePx={starSizePx} />
              <StarFillMask $fillWidth={fillWidth}>
                <RatingStarFill $sizePx={starSizePx} />
              </StarFillMask>
            </StarSlot>
          );
        })}
      </StarCluster>
      <RatingValue
        $fontSizePx={valueFontSizePx}
        $fontWeight={valueFontWeight}
        $valueTone={valueTone}
      >
        {valueLabel}
      </RatingValue>
    </RatingRow>
  );
}
